import {
  ArrowPathIcon,
  EyeIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { FileUpload } from '@/components/shared/FileUpload';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy';
import { PdfAttachment } from '@/data/hook/book/usePdfMaterialDraft';
import { formatFileSizeLabel } from '@/utils/file';
import { FileUploadStatus } from '@/components/shared/FileUpload/types';
import { UploadProgress } from '@/utils/shared/uploadProgress';
import { PDF_INTERACTIVE_ACCEPT, PDF_INTERACTIVE_MAX_SIZE } from '@/constants';

import PdfSavedFileMeta from './PdfSavedFileMeta';

type PdfMaterialFileFieldProps = {
  file?: File;
  attachment?: PdfAttachment;
  status?: FileUploadStatus;
  progress: UploadProgress;
  errorMessage?: string;
  onSelect: (file: File) => void;
  onCancelUpload: () => void;
  onRetry: () => void;
  onClear: () => void;
  onRemove: () => void;
};

export default function PdfMaterialFileField({
  file,
  attachment,
  status,
  progress,
  errorMessage,
  onSelect,
  onCancelUpload,
  onRetry,
  onClear,
  onRemove,
}: PdfMaterialFileFieldProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.materialsTab.pdfForm',
  });
  const { t: tFileUpload } = useTranslation('translation', {
    keyPrefix: 'fileUpload',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  if (attachment)
    return (
      <FileUpload.Item status="uploaded">
        <FileUpload.Item.Content>
          <FileUpload.Item.Name>{attachment.name}</FileUpload.Item.Name>
          <ConditionalRenderer condition={attachment.isNew}>
            <FileUpload.Item.Meta>
              {t('uploadedMeta', {
                size: formatFileSizeLabel(attachment.size ?? 0),
              })}
            </FileUpload.Item.Meta>
          </ConditionalRenderer>
          <ConditionalRenderer condition={!attachment.isNew}>
            <PdfSavedFileMeta url={attachment.url} />
          </ConditionalRenderer>
        </FileUpload.Item.Content>
        <FileUpload.Item.Actions>
          <Button
            type="button"
            size="sm"
            color="secondary"
            variant="soft"
            shape="square"
            aria-label={t('viewFile')}
            onClick={() =>
              window.open(attachment.url, '_blank', 'noopener,noreferrer')
            }
          >
            <EyeIcon className="size-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            color="error"
            variant="ghost"
            onClick={onRemove}
          >
            <TrashIcon className="size-4" />
            {tCommon('remove')}
          </Button>
        </FileUpload.Item.Actions>
      </FileUpload.Item>
    );

  if (!status || !file)
    return (
      <FileUpload.Dropzone
        accept={PDF_INTERACTIVE_ACCEPT}
        maxSize={PDF_INTERACTIVE_MAX_SIZE}
        onSelect={onSelect}
        title={t('dropzoneTitle')}
        description={t('dropzoneDescription')}
      />
    );

  return (
    <FileUpload.Item status={status}>
      <FileUpload.Item.Content>
        <FileUpload.Item.Name>{file.name}</FileUpload.Item.Name>
        <ConditionalRenderer condition={status === 'uploading'}>
          <FileUpload.Item.Meta>
            {tFileUpload('uploading', {
              loaded: formatFileSizeLabel(progress.loaded),
              total: formatFileSizeLabel(progress.total),
              percent: progress.percent,
            })}
          </FileUpload.Item.Meta>
          <FileUpload.Item.Progress value={progress.percent} />
        </ConditionalRenderer>
        <ConditionalRenderer condition={status === 'failed'}>
          <FileUpload.Item.Meta>
            {errorMessage ?? t('uploadFailed')}
          </FileUpload.Item.Meta>
        </ConditionalRenderer>
      </FileUpload.Item.Content>
      <ConditionalRenderer condition={status === 'uploading'}>
        <FileUpload.Item.Actions>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCancelUpload}
          >
            <XMarkIcon className="size-4" />
            {t('cancelUpload')}
          </Button>
        </FileUpload.Item.Actions>
      </ConditionalRenderer>
      <ConditionalRenderer condition={status === 'failed'}>
        <FileUpload.Item.Actions>
          <Button
            type="button"
            size="sm"
            color="primary"
            variant="soft"
            onClick={onRetry}
          >
            <ArrowPathIcon className="size-4" />
            {tCommon('tryAgain')}
          </Button>
          <Button
            type="button"
            size="sm"
            color="error"
            variant="ghost"
            aria-label={t('removeFile')}
            onClick={onClear}
          >
            <TrashIcon className="size-4" />
          </Button>
        </FileUpload.Item.Actions>
      </ConditionalRenderer>
    </FileUpload.Item>
  );
}
